import { createRoot } from "react-dom/client";
import { App as CapacitorApp, type AppState } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { forceHideSplashScreen } from "./lib/splash-screen";
import "./index.css";
import elefaAppIcon from "@/assets/elefa-app-icon.webp";

// Warm the brand icon cache before any route mounts so the Contacts/loading
// screens render the logo instantly instead of fetching it on first paint.
(() => {
  try {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = elefaAppIcon;
    (link as HTMLLinkElement & { fetchPriority?: string }).fetchPriority = "high";
    document.head.appendChild(link);
    const img = new Image();
    img.decoding = "async";
    img.src = elefaAppIcon;
  } catch {
    /* non-fatal */
  }
})();


// Native bundle verification marker - checked by scripts/rebuild-ios.sh
// to confirm the latest web build was copied into ios/App/App/public.
const NATIVE_STARTUP_VERSION = "native-startup-v2026-05-16-sipjs-mid-audio";
console.log(`[boot] ${NATIVE_STARTUP_VERSION}`);

const rootEl = document.getElementById("root");
const isNativeRuntime = Capacitor.isNativePlatform();

function renderFatal(message: string) {
  if (!rootEl) return;
  
  // Only show detailed errors in development mode
  const isDev = import.meta.env.DEV;
  
  // Log full error to console for debugging (even in production)
  console.error('Fatal application error:', message);
  console.error('[Fatal] Full details:', { message, stack: (message as any)?.stack, ua: navigator.userAgent });
  
  // User-friendly message for production
  const displayMessage = isDev 
    ? message 
    : 'An unexpected error occurred. Please refresh the page or contact support if the problem persists.';
  
  rootEl.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#0b0b0e;color:#ffffff;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;">
      <div style="max-width:600px;width:100%;text-align:center;">
        <div style="font-size:48px;margin-bottom:16px;">⚠️</div>
        <div style="font-size:20px;font-weight:700;margin-bottom:8px;">Something went wrong</div>
        <div style="opacity:.9;margin-bottom:24px;">We're sorry, but the application couldn't load properly.</div>
        ${isDev 
          ? `<pre style="text-align:left;white-space:pre-wrap;background:rgba(255,255,255,.08);padding:16px;border-radius:12px;overflow:auto;font-size:12px;">${displayMessage}</pre>` 
          : `<div style="opacity:.7;font-size:14px;margin-bottom:16px;">${displayMessage}</div>
             <button onclick="window.location.reload()" style="background:#3b82f6;color:white;border:none;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:500;cursor:pointer;">
               Refresh Page
             </button>`
        }
      </div>
    </div>
  `;
}

function shouldKeepNativeAppVisible(message: string): boolean {
  if (!isNativeRuntime) return false;
  return !isChunkLoadError(message);
}

// Filter recoverable DOM errors (React 18 + browser extensions/translation)
function isRecoverableDomError(msg: string): boolean {
  return /NotFoundError|removeChild|insertBefore|appendChild/.test(msg);
}

// Auto-reload on stale chunk errors (post-deployment)
function isChunkLoadError(msg: string): boolean {
  return /Failed to fetch dynamically imported module|Loading chunk|import.*failed|importing a module script failed/i.test(msg);
}

// Non-fatal service initialization errors that shouldn't crash the app
function isNonFatalServiceError(msg: string): boolean {
  return /twilio|twilsock|conversations|webrtc|push.*notification|voip|callkit|native.*plugin|plugin.*not.*implemented|unimplemented|service worker|SDK initialization|messaging.*provider|encryption|ratchet|chat.*service|MESSAGING_PROVIDER|missing sub claim|bad_jwt|token.*expired|Socket error|socket.*undefined|WebSocket|ws:\/\/|PROVIDER_FETCH_FAILED|PROVIDER_NOT_CONFIGURED|termination.*provider|@twilio_conversations|postgres_changes|realtime|channel.*subscribe|tried to subscribe multiple times|after subscribe/i.test(msg);
}

function isNonFatalNativeCapabilityError(msg: string): boolean {
  return /contacts?|CNContact|contact picker|Permission is required to access contacts|permission.*contacts|user.*cancel|cancelled|canceled|plugin.*contacts/i.test(msg);
}

let chunkReloadAttempted = false;

window.addEventListener("error", (e) => {
  const err = (e as ErrorEvent).error;
  const msg = err?.message || (e as ErrorEvent).message || String(err || '');
  const target = e.target as EventTarget | null;

  // Resource load failures (img/svg/font/media/link/script fetch failures) are
  // not React runtime crashes. iOS WKWebView can emit them for blocked/offline
  // flag images or native asset requests; rendering the fatal screen here hides
  // the entire app even though the UI can keep running.
  if (!err && target && target !== window) {
    const tagName = target instanceof Element ? target.tagName.toLowerCase() : 'resource';
    const resourceUrl = target instanceof Element
      ? target.getAttribute('src') || target.getAttribute('href') || ''
      : '';
    console.warn('[Resource load error suppressed]', tagName, resourceUrl);
    e.preventDefault();
    return;
  }

  if (!err && !msg) {
    console.warn('[Empty window error suppressed]', e);
    e.preventDefault();
    return;
  }

  // Also check the full error string for SDK patterns
  const fullMsg = msg + ' ' + (err?.stack || '') + ' ' + ((e as ErrorEvent).filename || '');
  if (isRecoverableDomError(msg)) {
    console.warn('[Recoverable DOM error suppressed]', msg);
    e.preventDefault();
    return;
  }
  if (isChunkLoadError(msg) && !chunkReloadAttempted) {
    chunkReloadAttempted = true;
    console.warn('[Stale chunk detected, reloading...]', msg);
    window.location.reload();
    return;
  }
  if (isNonFatalServiceError(msg) || isNonFatalServiceError(fullMsg)) {
    console.warn('[Non-fatal service error suppressed]', msg);
    e.preventDefault();
    return;
  }
  if (isNonFatalNativeCapabilityError(fullMsg)) {
    console.warn('[Non-fatal native capability error suppressed]', msg);
    e.preventDefault();
    return;
  }
  if (shouldKeepNativeAppVisible(fullMsg || msg)) {
    console.error('[Native runtime error preserved without replacing app]', err || msg);
    e.preventDefault();
    return;
  }
  renderFatal(err?.stack || err?.message || String((e as ErrorEvent).message || e));
});

window.addEventListener("unhandledrejection", (e) => {
  const reason = (e as PromiseRejectionEvent).reason;
  const msg = reason?.message || String(reason || '');
  const fullMsg = [
    msg,
    reason?.stack,
    reason?.code,
    reason?.name,
    reason && typeof reason === 'object' ? JSON.stringify(reason, Object.keys(reason).sort()) : '',
  ].filter(Boolean).join(' ');
  if (isRecoverableDomError(msg)) {
    console.warn('[Recoverable DOM rejection suppressed]', msg);
    e.preventDefault();
    return;
  }
  if (isChunkLoadError(msg) && !chunkReloadAttempted) {
    chunkReloadAttempted = true;
    console.warn('[Stale chunk detected, reloading...]', msg);
    window.location.reload();
    return;
  }
  // Don't crash the entire app for non-critical service init failures
  // (e.g. Twilio SDK, WebRTC, push notifications)
  if (isNonFatalServiceError(msg) || isNonFatalServiceError(fullMsg)) {
    console.warn('[Non-fatal service rejection suppressed]', msg || fullMsg);
    e.preventDefault();
    return;
  }
  if (isNonFatalNativeCapabilityError(msg) || isNonFatalNativeCapabilityError(fullMsg)) {
    console.warn('[Non-fatal native capability rejection suppressed]', msg || fullMsg);
    e.preventDefault();
    return;
  }
  if (shouldKeepNativeAppVisible(fullMsg || msg)) {
    // Serialize the reason fully — Capacitor's bridge stringifies `reason || msg`
    // as `{}` when reason is a non-Error object, hiding the real failure.
    let serialized = '';
    try {
      serialized = JSON.stringify(reason, Object.getOwnPropertyNames(reason || {}));
    } catch { /* circular */ }
    console.error(
      '[Native runtime rejection preserved without replacing app]',
      'msg=', msg,
      'name=', (reason as any)?.name,
      'code=', (reason as any)?.code,
      'stack=', (reason as any)?.stack,
      'json=', serialized,
      'raw=', reason,
    );
    e.preventDefault();
    return;
  }
  renderFatal(reason?.stack || reason?.message || String(reason));
});

function hideBootSplash() {
  const el = document.getElementById("boot-splash");
  if (!el) return;
  el.classList.add("boot-splash--hide");
  window.setTimeout(() => el.parentNode?.removeChild(el), 360);
}

function hideNativeSplashAgain() {
  if (!isNativeRuntime) return;
  void import("@capacitor/splash-screen")
    .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 120 }))
    .catch(() => {});
}

let __elefaResumeReloadAt = 0;
let __elefaReactTreePainted = false;
let __elefaVisibleSince = Date.now();
const ELEFA_RESUME_WATCHDOG_MS = 10_000;
const ELEFA_LOADING_SELECTOR = '[data-elefa-loading="true"]';
let __elefaResumeWatchdogTimer: number | null = null;

async function bootstrap() {
  try {
    if (!rootEl) throw new Error("Root element #root not found");
    const { default: App } = await import("./App.tsx");
    createRoot(rootEl).render(<App />);
    // Hand off from the inline boot splash to the React UI on the next frame
    // (after first paint) so users never see a blank screen on relaunch.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      __elefaReactTreePainted = rootEl.childElementCount > 0;
      hideBootSplash();
    }));
  } catch (e) {
    renderFatal(e instanceof Error ? e.stack || e.message : String(e));
  }
}

// White-screen-on-resume watchdog (2026-06-23).
// Both Android and iOS can keep the WebView alive while the user has the app
// backgrounded for a long time, but discard the rendered DOM under memory
// pressure. The native side already reloads on hard renderer-process death;
// this JS-side guard catches the softer case where the bridge is still alive
// but the React tree was wiped. On visibility -> visible we wait one frame
// and reload if #root has no children.
function __elefaCheckRootAfterResume() {
  try {
    hideNativeSplashAgain();
    // pageshow fires during normal web cold start, before the dynamic App import
    // and React's first commit can complete. Do not treat that startup window as
    // a white-screen failure, or the recovery reloads forever and leaves users on
    // the splash/icon screen.
    if (!__elefaReactTreePainted) return;
    const r = document.getElementById("root");
    if (!r || r.childElementCount === 0) {
      const now = Date.now();
      if (now - __elefaResumeReloadAt < 30000) return; // anti-loop
      __elefaResumeReloadAt = now;
      console.warn("[Resume] React root empty after foreground — reloading");
      window.location.replace(window.location.href);
    }
  } catch {/* non-fatal */}
}

function __elefaReloadFromResumeWatchdog(reason: string) {
  const now = Date.now();
  if (now - __elefaResumeReloadAt < 30000) return;
  __elefaResumeReloadAt = now;
  console.warn(`[Resume] ${reason} — reloading`);
  forceHideSplashScreen();
  window.location.reload();
}

function __elefaStartResumeWatchdog(source: "boot" | "resume") {
  if (__elefaResumeWatchdogTimer !== null) {
    clearTimeout(__elefaResumeWatchdogTimer);
    __elefaResumeWatchdogTimer = null;
  }
  __elefaResumeWatchdogTimer = window.setTimeout(() => {
    __elefaResumeWatchdogTimer = null;
    try {
      forceHideSplashScreen();
      const loaderVisible = document.querySelector(ELEFA_LOADING_SELECTOR) !== null;
      if (loaderVisible) {
        __elefaReloadFromResumeWatchdog(`Loader still visible after ${ELEFA_RESUME_WATCHDOG_MS}ms on ${source}`);
      }
    } catch {/* non-fatal */}
  }, ELEFA_RESUME_WATCHDOG_MS);
}

function __elefaCancelResumeWatchdog() {
  if (__elefaResumeWatchdogTimer !== null) {
    clearTimeout(__elefaResumeWatchdogTimer);
    __elefaResumeWatchdogTimer = null;
  }
}

function __elefaCheckStuckLoadingAfterResume() {
  try {
    hideNativeSplashAgain();
    if (!__elefaReactTreePainted) return;
    const bootSplash = document.getElementById("boot-splash");
    const brandedLoader = document.querySelector(ELEFA_LOADING_SELECTOR);
    if (!bootSplash && !brandedLoader) return;
    if (Date.now() - __elefaVisibleSince < 10000) return;
    __elefaReloadFromResumeWatchdog("App remained on loading screen after foreground");
  } catch {/* non-fatal */}
}

__elefaStartResumeWatchdog("boot");

if (isNativeRuntime) {
  void CapacitorApp.addListener("appStateChange", (state: AppState) => {
    if (state.isActive) {
      __elefaVisibleSince = Date.now();
      hideNativeSplashAgain();
      __elefaStartResumeWatchdog("resume");
      window.setTimeout(__elefaCheckRootAfterResume, 500);
    } else {
      __elefaCancelResumeWatchdog();
    }
  }).catch(() => {});
}

// Native lifecycle bridge — Android MainActivity.onResume() and the iOS
// ElefaLifecycle plugin both call window.__elefaHideSplash() directly via
// evaluateJavaScript, then fire 'elefa-native-resume'. We relay that to
// 'elefa-auth-reset' so AuthContext can re-run its session-restore timeout.
(window as unknown as { __elefaHideSplash?: () => void }).__elefaHideSplash = () => {
  forceHideSplashScreen();
};

window.addEventListener("elefa-native-resume", () => {
  __elefaVisibleSince = Date.now();
  hideNativeSplashAgain();
  __elefaStartResumeWatchdog("resume");
  window.setTimeout(__elefaCheckRootAfterResume, 500);
  window.dispatchEvent(new CustomEvent("elefa-auth-reset"));
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    __elefaVisibleSince = Date.now();
    hideNativeSplashAgain();
    __elefaStartResumeWatchdog("resume");
    window.setTimeout(__elefaCheckRootAfterResume, 500);
    window.setTimeout(__elefaCheckStuckLoadingAfterResume, 12000);
  } else {
    __elefaCancelResumeWatchdog();
  }
});
window.addEventListener("pageshow", () => {
  __elefaVisibleSince = Date.now();
  hideNativeSplashAgain();
  __elefaStartResumeWatchdog("resume");
  window.setTimeout(__elefaCheckRootAfterResume, 500);
  window.setTimeout(__elefaCheckStuckLoadingAfterResume, 12000);
});

void bootstrap();

